import React, { useState } from "react";
import { useSharedValue } from "../components/Constants/constants";
import {
  getUserIdFromLocalStorage,
  getExcelFile,
  getFileBytes,
} from "../API_Endpoints/APIEndpoints";
import { BASE_URL, businessunitName, pythonBaseUrl } from "../App_Settings/app_settings";
import { CircularProgress } from "@mui/material";

export const DownloadExcelFile = ({ fileName, title = "Excel", businessunit }) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadClick = async (e) => {
    e.preventDefault();

    if (!fileName) {
      alert("Error: No file identifier provided for download");
      return;
    }

    const confirmation = window.confirm(
      "\u26A0 Important Information:\nYou are downloading Excel to your local storage. Please delete this file later.\n\nClick 'OK' to proceed with download."
    );

    if (confirmation) {
      setIsDownloading(true);
      try {
        await getExcelFile(
          getUserIdFromLocalStorage().organizationId,
          getUserIdFromLocalStorage().userId,
          businessunit,
          fileName
        );
      } catch (error) {
        console.error("Error downloading Excel file:", error);
        alert("Failed to download Excel file. Please try again.");
      } finally {
        setIsDownloading(false);
      }
    }
  };

  return (
    <a
      href={`https://eformapi.complytaxforms.com/p/s/${fileName}.xltx`}
      download
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleDownloadClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 30,
        height: 30,
        textDecoration: "none",
      }}
    >
      {isDownloading ? (
        <CircularProgress size={20} thickness={6} style={{ color: "#2aa7e0" }} />
      ) : (
        <img
          title="Download Excel"
          src="/image4.png"
          alt="Excel"
          style={{ height: 30, width: 30, verticalAlign: "middle" }}
        />
      )}
    </a>
  );
};

export const DownloadXMLFile = ({ fileName, fileImportId, businessunit, title = "XML" }) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadClick = async (e) => {
    e.preventDefault();

    if (!fileImportId) {
      alert("Error: No file identifier provided for download");
      return;
    }

    const confirmation = window.confirm(
      "\u26A0 Important Information:\nYou are downloading XML to your local storage. Please delete this file later from your local storage.\n\nClick 'OK' to proceed with download."
    );

    if (confirmation) {
      setIsDownloading(true);
      try {
        const formData = new FormData();
        formData.append("filename", fileImportId);
        formData.append("organization_id", getUserIdFromLocalStorage().organizationId);
        formData.append("user_id", getUserIdFromLocalStorage().userId);
        formData.append("business_unit", businessunit || "Demo");

        const response = await fetch(`${pythonBaseUrl}/Excel/DownloadXmlData`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${getUserIdFromLocalStorage().token}`,
            accept: "*/*",
          },
          body: formData,
        });

        if (!response.ok) {
          throw new Error(`Failed to download XML: ${response.status}`);
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${fileName || fileImportId}.xml`;
        a.click();
        window.URL.revokeObjectURL(url);

        const downloadTime = new Date();
        localStorage.setItem("downloadTime", downloadTime);
      } catch (error) {
        console.error("Error downloading XML:", error);
        alert("Failed to download XML. Please try again.");
      } finally {
        setIsDownloading(false);
      }
    }
  };

  return (
    <span
      onClick={handleDownloadClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 30,
        height: 30,
        cursor: "pointer",
      }}
      title="Download XML"
    >
      {isDownloading ? (
        <CircularProgress size={20} thickness={6} style={{ color: "#2aa7e0" }} />
      ) : (
        <img src="/image5.png" alt="XML" style={{ width: 30, height: 30 }} />
      )}
    </span>
  );
};

export const ExcelIcon = ({ onclick }) => {
  return (
    <img
      title="Download Audit Report"
      src="/audit.png"
      alt="Excel"
      onClick={onclick}
      style={{
        height: 30,
        width: 30,
        verticalAlign: "middle",
        marginRight: 6,
        cursor: "pointer",
      }}
    />
  );
};

export const ViewPDf = ({ fileImportId, fileName }) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleDownloadPdf = async () => {
    try {
      if (!fileName) {
        alert("Cannot download: missing original file name.");
        return;
      }
      setIsLoading(true);
      const { organizationId, userId } = getUserIdFromLocalStorage();
      const extension = (fileName.split(".").pop() || "pdf").toLowerCase();
      // Match SecureDocumentViewer usage where fileType is often 'uploadfiles'
      const type = "uploadfiles";
      const data = await getFileBytes(
        organizationId,
        userId,
        fileImportId,
        fileName,
        extension,
        type
      );

      const blob = new Blob([data.fileBytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      // Open in a new tab instead of downloading
      window.open(url, "_blank");
      // Revoke the URL after a short delay to allow the tab to load
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (error) {
      console.error("Failed to download PDF from bytes:", error);
      alert("Failed to download PDF. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div onClick={isLoading ? undefined : handleDownloadPdf}>
      {" "}
      {isLoading ? (
        <CircularProgress size={22} style={{ color: "#2aa7e0", verticalAlign: "middle" }} />
      ) : (
        <img
          title="View PDF"
          src="/image6.png"
          alt="PDF"
          style={{
            height: 30,
            width: 30,
            verticalAlign: "middle",
            cursor: "pointer",
          }}
        />
      )}
    </div>
  );
};
export const formatDate = (value) => {
  // Handle null, undefined, or empty values
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  // Check if date is valid
  if (isNaN(date.getTime())) {
    console.warn("formatDate received invalid date:", value);
    return "-";
  }

  const month = date.toLocaleString("en-US", { month: "short" });
  const day = date.toLocaleString("en-US", { day: "2-digit" });
  const year = date.toLocaleString("en-US", { year: "numeric" });
  const hour24 = date.getHours();

  const minute = date.getMinutes();

  const amPm = hour24 < 12 ? "am" : "pm";

  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  const formattedHour = hour12 < 10 ? `0${hour12}` : hour12;
  const formattedMinute = minute < 10 ? `0${minute}` : minute;

  return `${month} ${day}, ${year} ${formattedHour}:${formattedMinute} ${amPm}`;
};

export const useNotifications = (newNotification) => {
  const { notifications, handleNotificationList } = useSharedValue();

  // const addNotification = (newNotification) => {
  const updatedNotifications = [...notifications, newNotification];
  handleNotificationList(updatedNotifications);
  // };
};

export const checkIsSuperAdmin = async () => {
  try {
    const userId = getUserIdFromLocalStorage().userId;
    const organizationId = getUserIdFromLocalStorage().organizationId;

    const response = await fetch(`${BASE_URL}/users/${userId}/${organizationId}`, {
      headers: {
        Authorization: `Bearer ${getUserIdFromLocalStorage().token}`,
      },
    });

    if (response.ok) {
      const user = await response.json();
      return user?.isSuperAdmin || false;
    }
    return false;
  } catch (error) {
    console.error("Error checking super admin status:", error);
    return false;
  }
};

// Function to check if the user is an admin
export const checkIsAdmin = async () => {
  try {
    const userId = getUserIdFromLocalStorage().userId;
    const organizationId = getUserIdFromLocalStorage().organizationId;
    const response = await fetch(`${BASE_URL}/users/${userId}/${organizationId}`, {
      headers: {
        Authorization: `Bearer ${getUserIdFromLocalStorage().token}`,
      },
    });
    if (response.ok) {
      const user = await response.json();
      // Assuming userRoles is an array of role objects with a name property
      return user?.userRoles?.some((role) => role.name === "Admin") || false;
    }
    return false;
  } catch (error) {
    console.error("Error checking admin status:", error);
    return false;
  }
};

// // Function to return all files list (example implementation)
// export const getAllFilesList = async (history) => {
//   try {
//     const userId = getUserIdFromLocalStorage().userId;
//     const organizationId = getUserIdFromLocalStorage().organizationId;
//     const token = getUserIdFromLocalStorage().token;
//     // Check user role
//     const userResponse = await fetch(`${BASE_URL}/users/${userId}/${organizationId}`, {
//       headers: { 'Authorization': `Bearer ${token}` }
//     });
//     if (userResponse.status === 401) {
//       handleLogout(history);
//       return [];
//     }
//     if (!userResponse.ok) return [];
//     const user = await userResponse.json();
//     if (user?.isSuperAdmin) {
//       // Super admin: get all files
//       const response = await fetch(`${BASE_URL}/GetAllFiles`, {
//         headers: { 'Authorization': `Bearer ${token}` }
//       });
//       if (response.status === 401) {
//         handleLogout(history);
//         return [];
//       }
//       if (response.ok) {
//         return await response.json();
//       }
//       return [];
//     } else if (user?.userRoles?.some(role => role.name === 'Admin')) {
//       // Admin: get all files and filter by organizationId
//       const response = await fetch(`${BASE_URL}/GetAllFiles`, {
//         headers: { 'Authorization': `Bearer ${token}` }
//       });
//       if (response.status === 401) {
//         handleLogout(history);
//         return [];
//       }
//       if (response.ok) {
//         const files = await response.json();
//         return files.filter(file => file.organizationId === organizationId);
//       }
//       return [];
//     } else {
//       // Other: get files for user and org
//       const response = await fetch(`${BASE_URL}/GetAllFiles/${userId}/${organizationId}`, {
//         headers: { 'Authorization': `Bearer ${token}` }
//       });
//       if (response.status === 401) {
//         handleLogout(history);
//         return [];
//       }
//       if (response.ok) {
//         return await response.json();
//       }
//       return [];
//     }
//   } catch (error) {
//     console.error('Error fetching all files list:', error);
//     return [];
//   }
// };
// Function to return all files list (example implementation)
export const getAllFilesList = async (
  history,
  defaultOrganization,
  defaultBusinessunit,
  defaultUser
) => {
  try {
    const userId = getUserIdFromLocalStorage().userId;
    const organizationId = getUserIdFromLocalStorage().organizationId;
    const token = getUserIdFromLocalStorage().token;

    const response = await fetch(`${BASE_URL}/FileData/GetAllFiles/${userId}/${organizationId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (response.status === 401) {
      handleLogout(history);
      return [];
    }
    if (response.ok) {
      const data = await response.json();
      if (defaultOrganization && defaultOrganization.name !== "") {
        const organization = await getAllFilesListWithOrganization(history, defaultOrganization);
        if (defaultBusinessunit && defaultBusinessunit !== `All - ${businessunitName}`) {
          return organization.filter((row) => row.businessUnit === defaultBusinessunit);
        } else {
          return organization;
        }
      } else if (defaultUser && defaultUser !== "") {
        if (defaultBusinessunit && defaultBusinessunit !== `All - ${businessunitName}`) {
          return data.filter(
            (row) => row.userID === defaultUser && row.businessUnit === defaultBusinessunit
          );
        } else {
          return data.filter((row) => row.userID === defaultUser.id);
        }
      } else {
        if (defaultBusinessunit && defaultBusinessunit !== `All - ${businessunitName}`) {
          return data.filter((row) => row.businessUnit === defaultBusinessunit);
        } else {
          return data;
        }
      }
    }
    return [];
  } catch (error) {
    console.error("Error fetching all files list:", error);
    return [];
  }
};
export const getAllFilesListWithOrganization = async (history, defaultOrganization) => {
  try {
    const userId = getUserIdFromLocalStorage().userId;
    const organizationId = getUserIdFromLocalStorage().organizationId;
    const token = getUserIdFromLocalStorage().token;

    const response = await fetch(
      `${BASE_URL}/FileData/GetAllFiles/${userId}/${defaultOrganization.id}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    if (response.status === 401) {
      handleLogout(history);
      return [];
    }
    if (response.ok) {
      return await response.json();
    }
    return [];
  } catch (error) {
    console.error("Error fetching all files list:", error);
    return [];
  }
};

export const handleLogout = (history) => {
  localStorage.removeItem("token");
  localStorage.removeItem("CopilotToken");
  history.replace("/login");
};

/**
 * Format date to MM/DD/YYYY format for XML generation
 * Handles multiple input formats and avoids timezone conversion issues
 * @param {string} dateStr - Input date string in various formats
 * @returns {string} - Date in MM/DD/YYYY format or original string if parsing fails
 */
export const formatDateForXML = (dateStr) => {
  if (!dateStr) return "";

  // Try to parse MM/DD/YYYY format first (US date format - already in target format)
  const usDatePattern = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
  const usDateMatch = dateStr.match(usDatePattern);

  if (usDateMatch) {
    const [, month, day, year] = usDateMatch;
    // Pad month and day with leading zeros and return in MM/DD/YYYY format
    const formattedDate = `${month.padStart(2, "0")}/${day.padStart(2, "0")}/${year}`;

    return formattedDate;
  }

  // Try to parse YYYY-MM-DD format and convert to MM/DD/YYYY
  const isoDatePattern = /^(\d{4})-(\d{2})-(\d{2})$/;
  const isoDateMatch = dateStr.match(isoDatePattern);
  if (isoDateMatch) {
    const [, year, month, day] = isoDateMatch;
    const formattedDate = `${month}/${day}/${year}`;

    return formattedDate;
  }

  // Try to format the date if it's in a standard format
  // Use UTC methods to avoid timezone issues
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      return dateStr;
    }
    // Use UTC methods to avoid timezone conversion and format as MM/DD/YYYY
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");
    const formattedDate = `${month}/${day}/${year}`;

    return formattedDate;
  } catch (e) {
    return dateStr;
  }
};
